import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMPTY = { name: '', address: '', city: '' };

export default function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/locations');
      setLocations(data.data);
    } catch { setError('Failed to load locations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      if (editId) {
        await api.put(`/locations/${editId}`, form);
        setSuccess('Location updated');
      } else {
        await api.post('/locations', form);
        setSuccess('Location created');
      }
      setForm(EMPTY); setEditId(null); setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving location');
    }
  };

  const handleEdit = (loc) => {
    setForm({ name: loc.name, address: loc.address || '', city: loc.city || '' });
    setEditId(loc._id);
    setShowForm(true);
  };

  const handleToggle = async (loc) => {
    try {
      await api.put(`/locations/${loc._id}`, { ...loc, isActive: !loc.isActive });
      load();
    } catch (err) { setError('Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try { await api.delete(`/locations/${id}`); setSuccess('Deleted'); load(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h3>Locations</h3>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 3 }}>
            Manage your branches or outlets. Staff and forms are tied to locations.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditId(null); setForm(EMPTY); }}>
          {showForm ? 'Cancel' : '+ Add Location'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="card mb-3">
          <h4 style={{ marginBottom: 16 }}>{editId ? 'Edit Location' : 'Add New Location'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label>Location Name *</label>
                <input className="form-control" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Downtown Branch" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input className="form-control" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g. Karachi" />
              </div>
            </div>
            <div className="form-group">
              <label>Address</label>
              <input className="form-control" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Street address (optional)" />
            </div>
            <button className="btn btn-primary" type="submit">{editId ? 'Save Changes' : 'Add Location'}</button>
          </form>
        </div>
      )}

      {loading ? <div className="spinner" /> : locations.length === 0 ? (
        <div className="card text-center" style={{ padding: 56 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>📍</div>
          <p className="text-muted">No locations yet. Add your first branch or outlet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {locations.map(loc => (
            <div key={loc._id} className="card" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: loc.isActive ? 'var(--primary-light)' : '#f1f5f9',
                    color: loc.isActive ? 'var(--primary)' : 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                  }}>
                    📍
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{loc.name}</div>
                    {loc.city && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{loc.city}</div>}
                  </div>
                </div>
                <span className={`badge ${loc.isActive ? 'badge-success' : 'badge-gray'}`}>
                  {loc.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {loc.address && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{loc.address}</p>
              )}

              <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '8px 0' }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--primary)' }}>{loc.staffCount}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Staff</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '8px 0' }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{loc.responseCount}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Responses</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => handleEdit(loc)}>Edit</button>
                <button
                  className="btn btn-sm"
                  style={{ background: loc.isActive ? '#fef9c3' : '#dcfce7', color: loc.isActive ? '#92400e' : '#15803d' }}
                  onClick={() => handleToggle(loc)}
                >
                  {loc.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(loc._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
